import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { getEnv } from "../config/env";
import {
  connectEld, listConnections, disconnectEld, rotateIngestToken, importRods, telematicsSummary,
  ELD_PROVIDERS, PROVIDER_LABELS, OAUTH_PROVIDERS,
  type EldConnection, type EldProvider, type TelematicsSummary,
} from "../lib/telematics";

const STATUS_STYLE: Record<string, string> = {
  active: "text-verified", pending: "text-[#c9a227]", disabled: "text-muted", error: "text-danger",
};

export function Telematics() {
  const [connections, setConnections] = useState<EldConnection[]>([]);
  const [summary, setSummary] = useState<TelematicsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState<EldProvider>("terminal");
  const [name, setName] = useState("");
  const [token, setToken] = useState<{ id: string; value: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function refresh() {
    const [c, s] = await Promise.all([listConnections(), telematicsSummary()]);
    setConnections(c.connections); setSummary(s);
  }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function connect(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setNote(null);
    try {
      const r = await connectEld({ provider, display_name: name || undefined });
      setToken({ id: r.connection.id, value: r.ingest_token });
      setNote(r.authorize.required ? r.authorize.message ?? null : "Connected. Point your ELD/aggregator webhook at the ingest URL below.");
      setName("");
      await refresh();
    } finally { setBusy(false); }
  }

  async function reveal(id: string) {
    setBusy(true);
    try { const r = await rotateIngestToken(id); setToken({ id, value: r.ingest_token }); setNote("New ingest token issued (the previous one stopped working)."); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    setBusy(true);
    try { await disconnectEld(id); if (token?.id === id) setToken(null); await refresh(); } finally { setBusy(false); }
  }

  async function onCsv(id: string, file: File | undefined) {
    if (!file) return;
    setBusy(true); setNote(null);
    try {
      const csv = await file.text();
      const r = await importRods(id, csv);
      setNote(`Imported ${r.imported} duty records from ${file.name}.`);
      await refresh();
    } catch { setNote("Couldn't import that CSV — is it a RODS export?"); }
    finally { setBusy(false); }
  }

  // Show the branded custom-domain endpoint (never the raw *.supabase.co host).
  const ingestUrl = token ? `${getEnv().VITE_FUNCTIONS_URL.replace(/\/$/, "")}/telematics/ingest` : "";

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Telematics & ELD"
        subtitle="Bring your own ELD — connect any provider or aggregator, or import a RODS log. All HOS, GPS, and engine data lands in one place."
        actions={summary && (
          <span className="font-mono text-[11px] text-muted" data-testid="telem-summary">
            {(summary.counts.position ?? 0) + (summary.counts.hos ?? 0) + (summary.counts.engine ?? 0)} records
          </span>
        )}
      />

      <Panel title="Connect a source">
        <form className="grid gap-2 sm:grid-cols-3" onSubmit={connect}>
          <select className={inputClass} value={provider} onChange={(e) => setProvider(e.target.value as EldProvider)} aria-label="Provider">
            {ELD_PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABELS[p]}{OAUTH_PROVIDERS.includes(p) ? " (OAuth)" : ""}</option>)}
          </select>
          <input className={inputClass} placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={busy} data-testid="connect">Connect</Button>
        </form>
        {note && <p className="mt-3 font-mono text-[11px] text-accent-strong" data-testid="telem-note">{note}</p>}
        {token && (
          <div className="mt-2 grid gap-1" data-testid="ingest-block">
            <span className="font-mono text-[11px] text-muted">Webhook URL</span>
            <code className="break-all rounded bg-surface-2 px-2 py-1 font-mono text-[11px]">{ingestUrl}</code>
            <span className="font-mono text-[11px] text-muted">Ingest token (header <code>x-ingest-token</code>)</span>
            <code className="break-all rounded bg-surface-2 px-2 py-1 font-mono text-[11px]" data-testid="ingest-token">{token.value}</code>
          </div>
        )}
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : connections.length === 0 ? (
        <p className="font-mono text-sm text-muted">No connections yet. Connect an aggregator, a provider, or a RODS-CSV source above.</p>
      ) : (
        <Panel title={`Connections (${connections.length})`}>
          <ul className="grid gap-3" data-testid="connection-list">
            {connections.map((c) => (
              <li key={c.id} className="grid gap-1.5 border-b border-line pb-3 text-sm last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold">{c.display_name || PROVIDER_LABELS[c.provider]}</span>
                  <span className={`font-mono text-[11px] font-bold uppercase ${STATUS_STYLE[c.status] ?? "text-muted"}`}>{c.status}</span>
                </div>
                <span className="font-mono text-[11px] text-muted">
                  {PROVIDER_LABELS[c.provider]} · {c.record_count ?? 0} records{c.last_sync_at ? ` · synced ${new Date(c.last_sync_at).toLocaleString()}` : " · never synced"}
                </span>
                {c.status !== "disabled" && (
                  <span className="flex flex-wrap items-center gap-3">
                    <button type="button" className="font-mono text-[11px] text-accent-strong hover:underline" disabled={busy} onClick={() => reveal(c.id)}>Show webhook token</button>
                    <label className="cursor-pointer font-mono text-[11px] text-accent-strong hover:underline">
                      Import RODS CSV
                      <input type="file" accept=".csv,text/csv" className="hidden" data-testid={`csv-${c.id}`}
                        onChange={(e) => onCsv(c.id, e.target.files?.[0])} />
                    </label>
                    <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy} onClick={() => remove(c.id)}>Disconnect</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
